
import React, { useState, useEffect, useCallback } from 'react';
import { AppSettings, DashboardCardVisibilityConfig, Employee } from '../types';
import { Button } from './Button';
import { DEFAULT_APP_SETTINGS } from '../constants';

interface AdminUICustomizationPageProps {
  settings: AppSettings;
  onSaveSettings: (updatedSettings: Partial<AppSettings>) => void;
  currentUser: Employee | null;
}

const PaintBrushSolidIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path d="M10.07 2.028L3.12 8.979A3 3 0 002.25 11.101V18a3 3 0 003 3h6.899a3 3 0 002.121-.879l6.951-6.951a1.06 1.06 0 000-1.499L11.57 2.028a1.06 1.06 0 00-1.5 0zm.028 2.121L16.08 10.13l-4.95 4.95-5.98-5.98 4.95-4.95zM4.5 12.472V18a.75.75 0 00.75.75h6.899a.75.75 0 00.53-.22l2.122-2.121H6.375a1.875 1.875 0 01-1.875-1.875v-.008z" />
    <path d="M15 3a.75.75 0 00-.75.75V6h1.5V3.75A.75.75 0 0015 3z" />
    <path d="M19.125 4.516a.75.75 0 00-1.042.018L12 10.575V12h1.425l6.043-6.043a.75.75 0 00.018-1.042l-.318-.318a.75.75 0 00-1.042-.018zM10.5 12V6.375L4.94 11.936A1.5 1.5 0 004.5 12v.001a1.5 1.5 0 000 2.998V15a1.5 1.5 0 001.5 1.5h.001a1.5 1.5 0 002.998 0H9a1.5 1.5 0 001.5-1.5zm5.46-.001l-2.086-2.086-2.136 2.135L12 12.252l.204.204 2.135-2.135 2.086 2.086.001.001a3.735 3.735 0 00.84-.155 3.751 3.751 0 002.53-4.216.75.75 0 10-1.45-.39c-.481 1.79-2.1 3.026-3.87 2.874z" />
  </svg>
);

export const AdminUICustomizationPage: React.FC<AdminUICustomizationPageProps> = ({ settings, onSaveSettings, currentUser }) => {
  const [formData, setFormData] = useState<AppSettings>(settings);
  const [logoPreview, setLogoPreview] = useState<string | null>(settings.logoBase64);

  useEffect(() => {
    const initialSettings = {
      ...DEFAULT_APP_SETTINGS, // Ensure all defaults are there
      ...settings,             // Override with passed settings
      dashboardCardVisibility: { // Deep merge for dashboardCardVisibility
        ...DEFAULT_APP_SETTINGS.dashboardCardVisibility,
        ...(settings.dashboardCardVisibility || {}),
      },
    };
    setFormData(initialSettings);
    setLogoPreview(initialSettings.logoBase64);
  }, [settings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        if (name.startsWith('dashboardCardVisibility.')) {
            const cardKey = name.split('.')[1] as keyof DashboardCardVisibilityConfig;
            setFormData(prev => ({
                ...prev,
                dashboardCardVisibility: {
                    ...(prev.dashboardCardVisibility || DEFAULT_APP_SETTINGS.dashboardCardVisibility),
                    [cardKey]: checked,
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: checked }));
        }
    } else { // Handles text and color inputs
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData(prev => ({ ...prev, logoBase64: base64String }));
        setLogoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logoBase64: null }));
    setLogoPreview(null);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveSettings(formData);
  };

  const handleResetUIDefaults = () => {
     if (window.confirm("Are you sure you want to reset UI settings (logo, colors, dashboard visibility) to their default values? Other settings will remain unchanged.")) {
        const newFormData = {
            ...formData, 
            logoBase64: DEFAULT_APP_SETTINGS.logoBase64,
            primaryColor: DEFAULT_APP_SETTINGS.primaryColor,
            accentColor: DEFAULT_APP_SETTINGS.accentColor,
            dashboardCardVisibility: DEFAULT_APP_SETTINGS.dashboardCardVisibility,
        };
        setFormData(newFormData);
        setLogoPreview(DEFAULT_APP_SETTINGS.logoBase64);
     }
  };
  
  const renderInput = (label: string, name: keyof AppSettings, type: string = "text") => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {type === "color" ? (
         <div className="flex items-center gap-2">
            <input
            type={type}
            id={name}
            name={name}
            value={String(formData[name])}
            onChange={handleChange}
            className="p-0 h-10 w-16 border border-slate-300 rounded-md shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
            />
            <input 
                type="text"
                value={String(formData[name])}
                onChange={handleChange}
                name={name}
                className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] sm:text-sm"
                placeholder="#RRGGBB"
            />
         </div>
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={String(formData[name])}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] sm:text-sm"
        />
      )}
    </div>
  );

  const renderCheckbox = (label: string, cardKey: keyof DashboardCardVisibilityConfig) => {
    const name = `dashboardCardVisibility.${cardKey}`;
    return (
        <div className="flex items-center">
            <input
                type="checkbox"
                id={name}
                name={name}
                checked={formData.dashboardCardVisibility?.[cardKey] ?? true}
                onChange={handleChange}
                className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]"
            />
            <label htmlFor={name} className="ml-2 block text-sm text-slate-700">
                {label}
            </label>
        </div>
    );
  };

  const renderSection = (title: string, children: React.ReactNode) => (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-slate-700 mb-5 border-b border-slate-200 pb-3">{title}</h2>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSave} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-1">
            <PaintBrushSolidIcon />
            <h1 className="text-2xl font-bold text-[var(--color-primary)]">UI Customization</h1>
        </div>
        <p className="text-sm text-slate-600">Personalize the look and feel of your Command Center.</p>
      </div>

      {renderSection("Branding", 
        <>
            <div>
                <label htmlFor="logoUpload" className="block text-sm font-medium text-slate-700 mb-1">Company Logo</label>
                <div className="flex items-center gap-4">
                    <input
                        type="file"
                        id="logoUpload"
                        name="logoUpload"
                        accept="image/png, image/jpeg, image/svg+xml, image/webp"
                        onChange={handleLogoChange}
                        className="block w-full max-w-xs text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-primary)] file:bg-opacity-10 file:text-[var(--color-primary)] hover:file:bg-opacity-20"
                    />
                    <p className="text-xs text-slate-500">Recommended: Transparent PNG/SVG. Max display height ~40px.</p>
                </div>
                {logoPreview && (
                    <div className="mt-4 p-2 border border-slate-200 rounded-md inline-block bg-slate-50">
                        <img src={logoPreview} alt="Logo Preview" className="h-20 max-w-xs object-contain" />
                        <Button type="button" variant="ghost" onClick={handleRemoveLogo} className="text-xs text-red-600 hover:text-red-700 mt-2">
                            Remove Logo
                        </Button>
                    </div>
                )}
            </div>
            {renderInput("Primary Brand Color", "primaryColor", "color")}
            {renderInput("Accent Color", "accentColor", "color")}
        </>
      )}

      {renderSection("Dashboard Card Visibility", 
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderCheckbox("Active Repairs Card", "activeRepairs")}
            {renderCheckbox("Revenue Summary Card", "revenueSummary")}
            {renderCheckbox("New Repairs Today Card", "newRepairsToday")}
            {renderCheckbox("New Tickets Card", "newTickets")}
            {renderCheckbox("In Progress Tickets Card", "inProgressTickets")}
            {renderCheckbox("Waiting Customer Approval Card", "waitingCustomerApprovalTickets")}
            {renderCheckbox("Waiting for Parts Card", "waitingForPartsTickets")}
            {renderCheckbox("Ready for Pickup Card", "readyForPickupTickets")}
            {renderCheckbox("Quick Appraisal Widget", "quickAppraisalWidget")}
            {renderCheckbox("Repair Type Breakdown Panel", "repairTypeBreakdown")}
            {renderCheckbox("New Check-ins Panel", "newCheckInsPanel")}
        </div>
      )}
      
      <div className="bg-white p-6 rounded-lg shadow-md mt-6 flex justify-end space-x-3">
        <Button type="button" variant="ghost" onClick={handleResetUIDefaults}>
          Reset UI Defaults
        </Button>
        <Button type="submit" variant="primary">
          Save All Settings
        </Button>
      </div>
    </form>
  );
};